import {useEffect, useMemo, useRef, useState} from "react";
import {
    Alert,
    Box,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material";
import {alpha} from "@mui/material/styles";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import MonetizationOnRoundedIcon from "@mui/icons-material/MonetizationOnRounded";
import SmsRoundedIcon from "@mui/icons-material/SmsRounded";
import TollRoundedIcon from "@mui/icons-material/TollRounded";
import {useTranslation} from "react-i18next";
import {useAdminDailyUsage, useAdminLegalEntities, useAdminUsers} from "../../api/admin/adminQueries";
import {renderer} from "../../util/renderer";
import SectionShell from "./AdminShared";
import {type DailyUsageStat} from "./AdminTypes";

type UsageMetricKey = "cost" | "totalTokens" | "inputTokens" | "outputTokens" | "messagesSent";

type UsageDay = DailyUsageStat & {cost: number};

const formatRangeLabel = (start: string, end: string) =>
    `${renderer.formatShortDate(start)} - ${renderer.formatShortDate(end)}`;

const formatUsageMetricValue = (metric: UsageMetricKey, value: number) => {
    if (metric === "cost") {
        return renderer.formatCurrency(value);
    }

    return renderer.formatNumber(value);
};

const chunkUsageDays = (days: UsageDay[]) => {
    const weeks: UsageDay[][] = [];

    for (let index = 0; index < days.length; index += 7) {
        weeks.push(days.slice(index, index + 7));
    }

    return weeks;
};

const UsageSummaryCard = ({
    title,
    value,
    helper,
    icon,
    tone,
}: {
    title: string;
    value: string;
    helper: string;
    icon: React.ReactNode;
    tone: "primary" | "secondary" | "success";
}) => (
    <Paper
        elevation={0}
        sx={(theme) => ({
            p: 2.5,
            borderRadius: 3,
            border: "1px solid",
            borderColor: alpha(theme.palette[tone].main, 0.18),
            background: `linear-gradient(180deg, ${alpha(theme.palette[tone].main, 0.12)}, ${alpha(theme.palette.background.paper, 0.94)})`,
        })}
    >
        <Stack spacing={2}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                    <Typography
                        sx={(theme) => ({
                            color: theme.palette.text.secondary,
                            fontSize: 12,
                            fontWeight: 700,
                            textTransform: "uppercase",
                            letterSpacing: 1,
                        })}
                    >
                        {title}
                    </Typography>
                </Box>
                <Box
                    sx={(theme) => ({
                        width: 42,
                        height: 42,
                        borderRadius: 2.5,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: theme.palette[tone].main,
                        bgcolor: alpha(theme.palette[tone].main, 0.12),
                    })}
                >
                    {icon}
                </Box>
            </Stack>
            <Typography
                sx={(theme) => ({
                    fontFamily: theme.typography.h4.fontFamily || theme.typography.fontFamily,
                    fontWeight: 900,
                    fontSize: 32,
                    lineHeight: 1,
                    color: theme.palette.text.primary,
                })}
            >
                {value}
            </Typography>
            <Typography color="text.secondary">{helper}</Typography>
        </Stack>
    </Paper>
);

const UsageTab = () => {
    const {t: translate} = useTranslation();
    const pendingWeekAnchorRef = useRef<"first" | "last" | null>(null);
    const currentDate = useMemo(() => new Date(), []);
    const currentMonth = currentDate.getMonth() + 1;
    const currentYear = currentDate.getFullYear();
    const minSelectableYear = currentYear - 1;
    const [selectedLegalEntityId, setSelectedLegalEntityId] = useState("");
    const [selectedUserId, setSelectedUserId] = useState("all");
    const [selectedMetric, setSelectedMetric] = useState<UsageMetricKey>("cost");
    const [selectedMonth, setSelectedMonth] = useState(String(currentMonth));
    const [selectedYear, setSelectedYear] = useState(String(currentYear));
    const [activeWeekIndex, setActiveWeekIndex] = useState(0);
    const hasSelectedLegalEntity = selectedLegalEntityId !== "";
    const usageMetricOptions = useMemo<{label: string; value: UsageMetricKey}[]>(
        () => [
            {label: translate("admin.usage.metrics.cost"), value: "cost"},
            {label: translate("admin.usage.metrics.totalTokens"), value: "totalTokens"},
            {label: translate("admin.usage.metrics.inputTokens"), value: "inputTokens"},
            {label: translate("admin.usage.metrics.outputTokens"), value: "outputTokens"},
            {label: translate("admin.usage.metrics.messagesSent"), value: "messagesSent"},
        ],
        [translate],
    );
    const legalEntitiesQuery = useAdminLegalEntities();
    const usageUsersQuery = useAdminUsers(selectedLegalEntityId || undefined);
    const usageQuery = useAdminDailyUsage(
        selectedLegalEntityId || undefined,
        selectedUserId === "all" ? undefined : selectedUserId,
        selectedYear,
        selectedMonth,
    );
    const usageDays = useMemo(
        () =>
            (usageQuery.data?.items ?? []).map((usageItem) => ({
                ...usageItem,
                //TODO: BENCH-13808 remove and get cost from BE
                cost: Number((Math.random() * 20).toFixed(2)),
            })),
        [usageQuery.data?.items],
    );
    const usageWeeks = useMemo(() => chunkUsageDays(usageDays), [usageDays]);
    const visibleWeek = usageWeeks[activeWeekIndex] ?? [];
    const maxVisibleValue = Math.max(...visibleWeek.map((day) => day[selectedMetric]), 1);
    const billingCycleTotalTokens = usageDays.reduce((sum, day) => sum + day.totalTokens, 0);
    const billingCycleMessagesSent = usageDays.reduce((sum, day) => sum + day.messagesSent, 0);
    const legalEntities = legalEntitiesQuery.data ?? [];
    const users = usageUsersQuery.data ?? [];
    const billingCycleStart = usageQuery.data?.cycleStart;
    const selectedMonthNumber = Number(selectedMonth);
    const selectedYearNumber = Number(selectedYear);
    const selectedLegalEntity = legalEntities.find((legalEntity) => legalEntity.id === selectedLegalEntityId);
    const maxSelectableMonth = selectedYearNumber === currentYear ? currentMonth : 12;
    const hasPreviousMonth = selectedYearNumber > minSelectableYear || selectedMonthNumber > 1;
    const hasNextMonth = selectedYearNumber < currentYear || selectedMonthNumber < currentMonth;
    const monthOptions = useMemo(
        () => [
            ...Array.from({length: maxSelectableMonth}, (_value, index) => {
                const monthValue = String(index + 1);

                return {
                    value: monthValue,
                    label: renderer.formatLocalizedDate(`${selectedYear}-${monthValue.padStart(2, "0")}-01`, {
                        month: "long",
                    }),
                };
            }),
        ],
        [maxSelectableMonth, selectedYear],
    );
    const yearOptions = useMemo(
        () => [
            {value: String(currentYear - 1), label: String(currentYear - 1)},
            {value: String(currentYear), label: String(currentYear)},
        ],
        [currentYear],
    );
    const selectedMonthLabel = monthOptions.find((monthOption) => monthOption.value === selectedMonth)?.label ?? "";
    const visibleWeekLabel = visibleWeek.length
        ? formatRangeLabel(visibleWeek[0].date, visibleWeek[visibleWeek.length - 1].date)
        : hasSelectedLegalEntity
          ? translate("admin.usage.loadingBillingCycle")
          : translate("admin.usage.selectLegalEntityToLoad");
    const canGoToPreviousRange = activeWeekIndex > 0 || hasPreviousMonth;
    const canGoToNextRange = activeWeekIndex < usageWeeks.length - 1 || hasNextMonth;

    useEffect(() => {
        if (pendingWeekAnchorRef.current === "first") {
            setActiveWeekIndex(0);
        } else {
            setActiveWeekIndex(Math.max(usageWeeks.length - 1, 0));
        }
        pendingWeekAnchorRef.current = null;
    }, [usageQuery.data?.cycleStart, usageWeeks.length]);

    useEffect(() => {
        if (selectedYearNumber === currentYear && selectedMonthNumber > currentMonth) {
            setSelectedMonth(String(currentMonth));
        }
    }, [currentMonth, currentYear, selectedMonthNumber, selectedYearNumber]);

    const errorMessage =
        legalEntitiesQuery.error?.message || usageUsersQuery.error?.message || usageQuery.error?.message;

    const updateSelectedMonth = (monthOffset: -1 | 1) => {
        const nextDate = new Date(selectedYearNumber, selectedMonthNumber - 1 + monthOffset, 1);

        setSelectedMonth(String(nextDate.getMonth() + 1));
        setSelectedYear(String(nextDate.getFullYear()));
    };

    const handleShowPreviousRange = () => {
        if (activeWeekIndex > 0) {
            setActiveWeekIndex((currentValue) => Math.max(currentValue - 1, 0));
            return;
        }

        if (!hasPreviousMonth) {
            return;
        }

        pendingWeekAnchorRef.current = "last";
        updateSelectedMonth(-1);
    };

    const handleShowNextRange = () => {
        if (activeWeekIndex < usageWeeks.length - 1) {
            setActiveWeekIndex((currentValue) => Math.min(currentValue + 1, usageWeeks.length - 1));
            return;
        }

        if (!hasNextMonth) {
            return;
        }

        pendingWeekAnchorRef.current = "first";
        updateSelectedMonth(1);
    };

    return (
        <SectionShell eyebrow={translate("admin.usage.eyebrow")} title={translate("admin.usage.title")}>
            <Stack spacing={3}>
                <Stack
                    direction={{xs: "column", xl: "row"}}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems={{xs: "stretch", xl: "flex-start"}}
                >
                    <Stack direction={{xs: "column", lg: "row"}} spacing={2} flexWrap="nowrap" sx={{minWidth: 0}}>
                        <FormControl fullWidth sx={{width: 340, flexShrink: 0}} disabled={legalEntitiesQuery.isLoading}>
                            <InputLabel id="usage-legal-entity-label">
                                {translate("admin.shared.legalEntity")}
                            </InputLabel>
                            <Select
                                labelId="usage-legal-entity-label"
                                label={translate("admin.shared.legalEntity")}
                                value={selectedLegalEntityId}
                                onChange={(event) => {
                                    setSelectedLegalEntityId(event.target.value);
                                    setSelectedUserId("all");
                                }}
                            >
                                {legalEntities.map((legalEntity) => (
                                    <MenuItem key={legalEntity.id} value={legalEntity.id}>
                                        {legalEntity.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl
                            fullWidth
                            sx={{width: 320, flexShrink: 0}}
                            disabled={!selectedLegalEntityId || usageUsersQuery.isLoading}
                        >
                            <InputLabel id="usage-user-label">{translate("admin.shared.user")}</InputLabel>
                            <Select
                                labelId="usage-user-label"
                                label={translate("admin.shared.user")}
                                value={selectedUserId}
                                onChange={(event) => setSelectedUserId(event.target.value)}
                            >
                                <MenuItem value="all">{translate("admin.shared.allUsers")}</MenuItem>
                                {users.map((user) => (
                                    <MenuItem key={user.id} value={user.id}>
                                        {user.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl fullWidth sx={{width: 260, flexShrink: 0}} disabled={!selectedLegalEntityId}>
                            <InputLabel id="usage-metric-label">{translate("admin.usage.dailyMetric")}</InputLabel>
                            <Select
                                labelId="usage-metric-label"
                                label={translate("admin.usage.dailyMetric")}
                                value={selectedMetric}
                                onChange={(event) => setSelectedMetric(event.target.value as UsageMetricKey)}
                            >
                                {usageMetricOptions.map((metric) => (
                                    <MenuItem key={metric.value} value={metric.value}>
                                        {metric.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>

                    <Stack
                        direction={{xs: "column", sm: "row"}}
                        spacing={2}
                        justifyContent="flex-end"
                        alignItems={{xs: "stretch", sm: "center"}}
                        sx={{width: {xs: "100%", xl: "auto"}, flexShrink: 0}}
                    >
                        <FormControl fullWidth sx={{width: 220, flexShrink: 0}}>
                            <InputLabel id="usage-month-label">{translate("admin.shared.month")}</InputLabel>
                            <Select
                                labelId="usage-month-label"
                                label={translate("admin.shared.month")}
                                value={selectedMonth}
                                onChange={(event) => setSelectedMonth(event.target.value)}
                            >
                                {monthOptions.map((monthOption) => (
                                    <MenuItem key={monthOption.value} value={monthOption.value}>
                                        {monthOption.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl fullWidth sx={{width: 160, flexShrink: 0}}>
                            <InputLabel id="usage-year-label">{translate("admin.shared.year")}</InputLabel>
                            <Select
                                labelId="usage-year-label"
                                label={translate("admin.shared.year")}
                                value={selectedYear}
                                onChange={(event) => setSelectedYear(event.target.value)}
                            >
                                {yearOptions.map((yearOption) => (
                                    <MenuItem key={yearOption.value} value={yearOption.value}>
                                        {yearOption.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>
                </Stack>

                {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}

                {!hasSelectedLegalEntity ? (
                    <Paper
                        elevation={0}
                        sx={(theme) => ({
                            p: {xs: 3, md: 4},
                            borderRadius: 3,
                            border: "1px dashed",
                            borderColor: alpha(theme.palette.primary.main, 0.28),
                            bgcolor: alpha(
                                theme.palette.background.paper,
                                theme.palette.mode === "light" ? 0.88 : 0.94,
                            ),
                        })}
                    >
                        <Stack spacing={1.5} alignItems="center" textAlign="center">
                            <InsightsRoundedIcon sx={{fontSize: 40, color: "primary.main"}} />
                            <Typography sx={{fontWeight: 800, color: "text.primary"}}>
                                {translate("admin.usage.emptyState.title")}
                            </Typography>
                            <Typography color="text.secondary" sx={{maxWidth: 520}}>
                                {translate("admin.usage.emptyState.description")}
                            </Typography>
                        </Stack>
                    </Paper>
                ) : (
                    <>
                        <Alert
                            severity="info"
                            sx={(theme) => ({
                                borderRadius: 3,
                                bgcolor: alpha(
                                    theme.palette.primary.main,
                                    theme.palette.mode === "light" ? 0.08 : 0.16,
                                ),
                                color: theme.palette.text.primary,
                                ".MuiAlert-icon": {color: theme.palette.primary.main},
                                alignItems: "center",
                            })}
                        >
                            {translate("admin.usage.selectionSummary", {
                                month: selectedMonthLabel,
                                year: selectedYear,
                                legalEntityName:
                                    selectedLegalEntity?.name ?? translate("admin.usage.selectedLegalEntityFallback"),
                            })}
                        </Alert>

                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: {xs: "1fr", md: "repeat(3, minmax(0, 1fr))"},
                                gap: 2,
                            }}
                        >
                            <UsageSummaryCard
                                title={translate("admin.usage.summary.costThisCycle")}
                                //TODO: BENCH-13808 remove and get cost from BE
                                value={renderer.formatCurrency(999.99)}
                                helper={
                                    billingCycleStart
                                        ? translate("admin.usage.summary.billingCycleStarted", {
                                              date: renderer.formatShortDate(billingCycleStart),
                                          })
                                        : translate("admin.usage.loadingBillingCycle")
                                }
                                icon={<MonetizationOnRoundedIcon />}
                                tone="secondary"
                            />
                            <UsageSummaryCard
                                title={translate("admin.dashboard.messagesSent")}
                                value={renderer.formatNumber(billingCycleMessagesSent)}
                                helper={
                                    selectedUserId === "all"
                                        ? translate("admin.usage.summary.aggregatedAcrossAllUsers")
                                        : translate("admin.usage.summary.filteredToSelectedUser")
                                }
                                icon={<SmsRoundedIcon />}
                                tone="primary"
                            />
                            <UsageSummaryCard
                                title={translate("admin.usage.summary.totalTokens")}
                                value={renderer.formatNumber(billingCycleTotalTokens)}
                                helper={
                                    selectedUserId === "all"
                                        ? translate("admin.usage.summary.allTokenUsage")
                                        : translate("admin.usage.summary.selectedUserTokenUsage")
                                }
                                icon={<TollRoundedIcon />}
                                tone="success"
                            />
                        </Box>

                        <Paper
                            elevation={0}
                            sx={(theme) => ({
                                p: {xs: 2.5, md: 3},
                                borderRadius: 3,
                                border: "1px solid",
                                borderColor: theme.palette.divider,
                                bgcolor: alpha(
                                    theme.palette.background.paper,
                                    theme.palette.mode === "light" ? 0.88 : 0.94,
                                ),
                            })}
                        >
                            <Stack spacing={3}>
                                <Stack
                                    direction={{xs: "column", md: "row"}}
                                    justifyContent="space-between"
                                    alignItems={{xs: "flex-start", md: "center"}}
                                    spacing={2}
                                >
                                    <Box>
                                        <Typography
                                            sx={(theme) => ({
                                                fontFamily:
                                                    theme.typography.h4.fontFamily || theme.typography.fontFamily,
                                                fontSize: 24,
                                                fontWeight: 800,
                                                color: theme.palette.text.primary,
                                            })}
                                        >
                                            {translate("admin.usage.dailyUsage")}
                                        </Typography>
                                        <Typography color="text.secondary">{visibleWeekLabel}</Typography>
                                    </Box>

                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <Tooltip
                                            title={
                                                activeWeekIndex > 0
                                                    ? translate("admin.usage.previousWeek")
                                                    : translate("admin.usage.previousMonth")
                                            }
                                        >
                                            <span>
                                                <IconButton
                                                    onClick={handleShowPreviousRange}
                                                    disabled={
                                                        usageQuery.isLoading ||
                                                        !hasSelectedLegalEntity ||
                                                        !canGoToPreviousRange
                                                    }
                                                >
                                                    <ChevronLeftRoundedIcon />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        <Tooltip
                                            title={
                                                activeWeekIndex < usageWeeks.length - 1
                                                    ? translate("admin.usage.nextWeek")
                                                    : translate("admin.usage.nextMonth")
                                            }
                                        >
                                            <span>
                                                <IconButton
                                                    onClick={handleShowNextRange}
                                                    disabled={
                                                        usageQuery.isLoading ||
                                                        !hasSelectedLegalEntity ||
                                                        !canGoToNextRange
                                                    }
                                                >
                                                    <ChevronRightRoundedIcon />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </Stack>
                                </Stack>

                                <Box
                                    sx={{
                                        display: "grid",
                                        gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
                                        gap: 1.5,
                                        alignItems: "end",
                                        minHeight: 300,
                                    }}
                                >
                                    {Array.from({length: 7}, (_, index) => visibleWeek[index] ?? null).map(
                                        (day, index) => {
                                            if (!day) {
                                                return (
                                                    <Box
                                                        key={`usage-placeholder-${index}`}
                                                        sx={{
                                                            borderRadius: 2.5,
                                                            minHeight: 240,
                                                            border: "1px dashed",
                                                            borderColor: "divider",
                                                            opacity: 0.3,
                                                        }}
                                                    />
                                                );
                                            }

                                            const value = day[selectedMetric];
                                            const heightPercentage = Math.max((value / maxVisibleValue) * 100, 10);

                                            return (
                                                <Stack
                                                    key={day.date}
                                                    spacing={1}
                                                    alignItems="stretch"
                                                    sx={{minWidth: 0}}
                                                >
                                                    <Tooltip
                                                        title={translate("admin.usage.metricOnDate", {
                                                            value: formatUsageMetricValue(selectedMetric, value),
                                                            date: renderer.formatLocalizedDate(day.date, {
                                                                weekday: "short",
                                                                month: "short",
                                                                day: "numeric",
                                                            }),
                                                        })}
                                                    >
                                                        <Box
                                                            sx={{
                                                                height: 220,
                                                                display: "flex",
                                                                alignItems: "flex-end",
                                                            }}
                                                        >
                                                            <Box
                                                                sx={(theme) => ({
                                                                    width: "100%",
                                                                    height: `${heightPercentage}%`,
                                                                    minHeight: 24,
                                                                    borderRadius: 3,
                                                                    background: `linear-gradient(180deg, ${alpha(theme.palette.primary.light, 0.92)}, ${theme.palette.primary.main})`,
                                                                    border: "1px solid",
                                                                    borderColor: alpha(
                                                                        theme.palette.primary.dark,
                                                                        0.22,
                                                                    ),
                                                                    boxShadow: `0 14px 26px ${alpha(theme.palette.primary.main, 0.18)}`,
                                                                })}
                                                            />
                                                        </Box>
                                                    </Tooltip>
                                                    <Typography
                                                        noWrap
                                                        sx={{
                                                            fontWeight: 700,
                                                            textAlign: "center",
                                                            color: "text.primary",
                                                        }}
                                                    >
                                                        {renderer.formatShortDate(day.date)}
                                                    </Typography>
                                                    <Typography
                                                        noWrap
                                                        variant="body2"
                                                        sx={{textAlign: "center", color: "text.secondary"}}
                                                    >
                                                        {formatUsageMetricValue(selectedMetric, value)}
                                                    </Typography>
                                                </Stack>
                                            );
                                        },
                                    )}
                                </Box>
                                {usageQuery.isLoading ? (
                                    <Typography color="text.secondary">
                                        {translate("admin.usage.loadingUsage")}
                                    </Typography>
                                ) : null}
                            </Stack>
                        </Paper>
                    </>
                )}
            </Stack>
        </SectionShell>
    );
};

export default UsageTab;
